import { type QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { apiClient } from '../axios.js';
import { toast } from '../components/ui/toast.js';
import { ALERT_NO_LONGER_ACTIVE_HTTP_STATUSES } from '../constants/device-alerts.js';
import { stopAlertSoundForAlert } from '../lib/alert-sound-player.js';
import { normalizeDeviceAlerts, rememberAcknowledgedDeviceAlert } from '../lib/device-alerts.js';
import type { Device } from '../types/device.js';

interface DevicesPage {
  devices: Device[];
  total: number;
}

interface AcknowledgeDeviceAlertVariables {
  alertId: string;
}

interface UseAcknowledgeDeviceAlertOptions {
  deviceId: string;
  noLongerActiveMessage: string;
  failedMessage: string;
}

/** Removes one alert from every cached `devices` query page for the given device, in place. */
export const removeAlertFromDevicesCache = (
  queryClient: QueryClient,
  deviceId: string,
  alertId: string,
): void => {
  queryClient.setQueriesData<DevicesPage>({ queryKey: ['devices'] }, (page) =>
    page
      ? {
          ...page,
          devices: page.devices.map((device) =>
            device.id === deviceId
              ? {
                  ...device,
                  alerts: normalizeDeviceAlerts(device.alerts).filter(
                    (alert) => alert.id !== alertId,
                  ),
                }
              : device,
          ),
        }
      : page,
  );
};

// zahrada returns 404/409 once the alert is no longer active (already acknowledged, resolved, or
// expired). That is not a failure the operator needs to retry -- it converges the same as a 200.
const isAlertNoLongerActiveStatus = (status: number | undefined): boolean =>
  status !== undefined &&
  (ALERT_NO_LONGER_ACTIVE_HTTP_STATUSES as readonly number[]).includes(status);

const acknowledgeDeviceAlert = async (alertId: string): Promise<void> => {
  await apiClient.post(`/alerts/${alertId}/acknowledge`);
};

export const useAcknowledgeDeviceAlert = ({
  deviceId,
  noLongerActiveMessage,
  failedMessage,
}: UseAcknowledgeDeviceAlertOptions) => {
  const queryClient = useQueryClient();

  const dismissAcknowledgedAlert = (alertId: string) => {
    removeAlertFromDevicesCache(queryClient, deviceId, alertId);
    rememberAcknowledgedDeviceAlert(alertId);
    stopAlertSoundForAlert(alertId);
  };

  // Callbacks live here, in the mutation's own options, never in mutate()'s second argument:
  // acknowledging removes the row (and on the last alert, the whole indicator), which unmounts
  // the observer that issued the call, and TanStack drops per-call callbacks for an unmounted one.
  return useMutation({
    mutationFn: ({ alertId }: AcknowledgeDeviceAlertVariables) => acknowledgeDeviceAlert(alertId),
    onSuccess: (_data, { alertId }) => {
      dismissAcknowledgedAlert(alertId);
    },
    onError: (error: unknown, { alertId }) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (isAlertNoLongerActiveStatus(status)) {
        dismissAcknowledgedAlert(alertId);
        toast(noLongerActiveMessage);
        return;
      }
      toast.error(failedMessage);
    },
  });
};
